// Панель состояния, краткая панель и копии (002: contracts/api.md, «Сопровождение»; FR-046,
// FR-048, FR-049). Краткая панель администратора клиента — без кодов, номеров операций и типов
// задач (FR-049).
import { z } from '../kernel/zod.ts';
import { IdSchema, TimestampSchema } from '../kernel/common.ts';
import { AlertChannelSchema, AlertSchema } from './alert.ts';
import { RestoreCheckResultSchema, RestoreCheckSchema } from './deployment.ts';
import { JobStateSchema } from './job.ts';
import { MailChannelStatusSchema } from '../delivery/connection.ts';

/**
 * Место на диске словами: «42,0 ГБ», «512 МБ», «900 Б» — единицы по 1024. Одни слова на
 * «Состоянии» и в оповещении «Мало места на диске копий».
 */
export function formatBytes(bytes: number): string {
  const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = unit === 0 || value >= 100 ? 0 : 1;
  const number = value.toLocaleString('ru-RU', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  return `${number} ${units[unit]}`;
}

/** Байтов в гигабайте порога alerts.backupDiskMinFreeGb — по 1024, как в formatBytes. */
export const GB_BYTES = 1024 ** 3;

export const backupResults = ['ok', 'failed'] as const;
export const BackupResultSchema = z.enum(backupResults);

/** Итог копии (observability.backups): пишет deploy/backup.sh. */
export const BackupSchema = z.object({
  id: IdSchema,
  finishedAt: TimestampSchema,
  /** daily — ночная, before-<версия> — перед выкаткой, manual — вручную. */
  reason: z.string(),
  result: BackupResultSchema,
  durationS: z.number().int().nonnegative(),
  /** Имя законченной копии; нет — копия не сделана. */
  backupRef: z.string().optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
  /** ok — что сделано; failed — причина и что делать. */
  notes: z.string(),
  /** Свободное место на диске копий в момент копии. */
  diskFreeBytes: z.number().int().nonnegative().optional(),
});
export type Backup = z.infer<typeof BackupSchema>;

/** Последняя копия или проверка восстановления: когда и итог. */
export const LastCheckSchema = z.object({ at: TimestampSchema, result: RestoreCheckResultSchema });
export type LastCheck = z.infer<typeof LastCheckSchema>;

/** ok — работает; degraded — есть действующие сбои; down — обработчик задач не отвечает. */
export const serviceStates = ['ok', 'degraded', 'down'] as const;
export const ServiceStateSchema = z.enum(serviceStates);

/** GET /api/ops/health — краткая панель (FR-049). */
export const HealthBriefResponseSchema = z.object({
  service: ServiceStateSchema,
  lastBackup: LastCheckSchema.nullable(),
  lastRestoreCheck: LastCheckSchema.nullable(),
  activeAlerts: z.number().int().nonnegative(),
});
export type HealthBriefResponse = z.infer<typeof HealthBriefResponseSchema>;

/** Процессы с пульсом (observability.pulses). */
export const pulseProcesses = ['worker', 'alerter'] as const;
export const PulseProcessSchema = z.enum(pulseProcesses);

export const PulseSchema = z.object({
  process: PulseProcessSchema,
  at: TimestampSchema,
  version: z.string(),
});

/**
 * Догон пропущенных запусков (research, пункт 2; FR-012): latest — за все пропущенные моменты один
 * запуск; each — каждый момент, но не больше 10 за раз (FR-012а).
 */
export const scheduleCatchUps = ['latest', 'each'] as const;
export const ScheduleCatchUpSchema = z.enum(scheduleCatchUps);
export type ScheduleCatchUp = z.infer<typeof ScheduleCatchUpSchema>;

export const ScheduleStatusSchema = z.object({
  name: z.string(),
  /** Правило cron из 5 полей в часовом поясе установки (FR-011): «30 3 * * *». */
  rule: z.string(),
  /** Догон пропущенных запусков (FR-012). */
  catchUp: ScheduleCatchUpSchema,
  /** Окно актуальности, секунд: пропущенный запуск старше — не догоняется (FR-014). */
  windowSeconds: z.number().int().positive(),
  /** Последнее наступившее время; нет — расписание ещё не запускалось. */
  lastDueAt: TimestampSchema.nullable(),
  /** Состояние последней поставленной задачи. */
  lastResult: JobStateSchema.nullable(),
  /** Последний пропущенный устаревший запуск и причина (FR-014). */
  lastSkippedAt: TimestampSchema.optional(),
  lastSkipReason: z.string().optional(),
});
export type ScheduleStatus = z.infer<typeof ScheduleStatusSchema>;

export const ChannelStatusSchema = z.object({
  channel: AlertChannelSchema,
  /** Канал без настроек в env стенда — «не настроен», это не сбой (FR-044ж). */
  configured: z.boolean(),
  lastSentAt: TimestampSchema.nullable(),
  /** 3 неудачные доставки подряд — «канал не работает». */
  failing: z.boolean(),
});
export type ChannelStatus = z.infer<typeof ChannelStatusSchema>;

/** GET /api/ops/status — панель состояния сопровождения (FR-046). */
export const StatusResponseSchema = z.object({
  pulses: z.array(PulseSchema),
  /** Пульс старше стольких минут — «Не отвечает» (alerts.noPulseMinutes, FR-037). */
  noPulseMinutes: z.number().int().positive(),
  queue: z.object({
    queued: z.number().int().nonnegative(),
    running: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
  }),
  schedules: z.array(ScheduleStatusSchema),
  lastBackup: LastCheckSchema.nullable(),
  lastRestoreCheck: LastCheckSchema.nullable(),
  backupDiskFreeBytes: z.number().int().nonnegative().nullable(),
  /** Порог места на диске копий (alerts.backupDiskMinFreeGb): меньше — «Мало места». */
  backupDiskMinFreeBytes: z.number().int().positive(),
  build: z.object({ version: z.string(), commit: z.string() }),
  /** Действующие оповещения. */
  alerts: z.array(AlertSchema),
  channels: z.array(ChannelStatusSchema),
  /** Канал почты сервиса — письма сотрудникам (008, FR-032), не каналы оповещений. */
  mail: MailChannelStatusSchema,
});
export type StatusResponse = z.infer<typeof StatusResponseSchema>;

/** Проверка восстановления на экране «Копии»: с числом записей восстановленной базы. */
export const BackupRestoreCheckSchema = RestoreCheckSchema.extend({
  /** Записей в восстановленной базе — из итога deploy/restore-check.sh; у неудачной нет. */
  records: z.number().int().nonnegative().optional(),
  /**
   * Протоколы в восстановленной базе (016: EXPORT-06): пункты, события истории, строки ленты,
   * снимки недели и записи команд — из итога deploy/restore-check.sh; у неудачной и старой нет.
   */
  minutes: z
    .object({
      items: z.number().int().nonnegative(),
      events: z.number().int().nonnegative(),
      revisions: z.number().int().nonnegative(),
      snapshots: z.number().int().nonnegative(),
      commands: z.number().int().nonnegative(),
    })
    .optional(),
});
export type BackupRestoreCheck = z.infer<typeof BackupRestoreCheckSchema>;

/**
 * GET /api/ops/backups — копии и проверки восстановления, новые сверху (FR-048). Итог и период
 * сужают оба списка; курсор листает копии, проверки — на первой странице.
 */
export const BackupsResponseSchema = z.object({
  backups: z.array(BackupSchema),
  restoreChecks: z.array(BackupRestoreCheckSchema),
  nextCursor: z.string().nullable(),
});
export type BackupsResponse = z.infer<typeof BackupsResponseSchema>;
